import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PenTool, Upload, Trash2, CheckCircle2, AlertTriangle } from "lucide-react";

/**
 * Allows the logged-in instructor/admin to upload their handwritten signature PNG/JPG.
 * Backend endpoints: POST/GET/DELETE /api/users/me/signature
 */
export default function InstructorSignature() {
  const [state, setState] = useState({ has_signature: false });
  const [err, setErr] = useState("");
  const [flash, setFlash] = useState("");
  const [busy, setBusy] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const fileRef = useRef(null);

  const refresh = async () => {
    try {
      const r = await api.get("/users/me/signature");
      setState(r.data || {});
      if (r.data?.has_signature) {
        try {
          const img = await api.get("/users/me/signature/preview", { responseType: "blob" });
          const url = URL.createObjectURL(img.data);
          setPreviewUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return url; });
        } catch { setPreviewUrl(""); }
      } else {
        setPreviewUrl("");
      }
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => { refresh(); }, []);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setErr(""); setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/users/me/signature", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setFlash("Firma cargada correctamente.");
      setTimeout(() => setFlash(""), 3000);
      await refresh();
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async () => {
    if (!confirm("¿Eliminar tu firma cargada?")) return;
    setBusy(true); setErr("");
    try {
      await api.delete("/users/me/signature");
      setFlash("Firma eliminada.");
      setTimeout(() => setFlash(""), 3000);
      await refresh();
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="instructor-signature" className="bg-white border border-gris/30 rounded-xl p-6 shadow-sm max-w-2xl">
      <div className="flex items-center gap-2 mb-2">
        <PenTool className="w-5 h-5 text-naranja" />
        <h3 className="font-heading font-bold text-marino text-lg">Mi firma digital</h3>
      </div>
      <p className="text-sm text-marino/70 mb-5">
        Sube una imagen PNG o JPG (fondo transparente recomendado, máx. 2 MB) con tu firma manuscrita.
        Aparecerá automáticamente en los certificados de los cursos que impartes.
      </p>

      {flash && (
        <div className="mb-4 flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 text-sm px-3 py-2 rounded-lg">
          <CheckCircle2 className="w-4 h-4" /> {flash}
        </div>
      )}
      {err && (
        <div className="mb-4 flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded-lg">
          <AlertTriangle className="w-4 h-4 mt-0.5" /> <span>{err}</span>
        </div>
      )}

      <div className="border-2 border-dashed border-gris/40 rounded-xl p-6 flex flex-col items-center justify-center min-h-[180px]">
        {state.has_signature ? (
          <img
            data-testid="signature-preview"
            src={previewUrl}
            alt="Mi firma"
            className="max-h-32 object-contain"
            onError={() => setPreviewUrl("")}
          />
        ) : (
          <p className="text-marino/50 text-sm text-center">
            Aún no has cargado tu firma.<br />La línea del certificado quedará vacía en tus cursos.
          </p>
        )}
      </div>

      <div className="mt-4 flex flex-wrap gap-3">
        <input
          ref={fileRef}
          data-testid="signature-input"
          type="file"
          accept="image/png,image/jpeg"
          onChange={onFile}
          className="hidden"
        />
        <button
          data-testid="signature-upload-btn"
          disabled={busy}
          onClick={() => fileRef.current?.click()}
          className="inline-flex items-center gap-2 bg-marino text-white px-4 py-2 rounded-lg font-medium hover:bg-marino/90 disabled:opacity-60"
        >
          <Upload className="w-4 h-4" /> {state.has_signature ? "Reemplazar firma" : "Subir firma"}
        </button>
        {state.has_signature && (
          <button
            data-testid="signature-delete-btn"
            disabled={busy}
            onClick={remove}
            className="inline-flex items-center gap-2 border border-red-300 text-red-600 px-4 py-2 rounded-lg font-medium hover:bg-red-50 disabled:opacity-60"
          >
            <Trash2 className="w-4 h-4" /> Eliminar
          </button>
        )}
      </div>
    </div>
  );
}
